import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../hooks/useNotification';
import { apiClient } from '../../services/apiClient';
import './ChangePasswordModal.css';

interface ChangePasswordModalProps {
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { showSuccess, showError, NotificationContainer } = useNotification();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setValidationError('');

    if (newPassword.length < 6) {
      setValidationError(t('changePassword.passwordTooShort'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError(t('changePassword.passwordMismatch'));
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.changePassword(currentPassword, newPassword);
      showSuccess(t('changePassword.success'));
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('changePassword.error');
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = currentPassword.length > 0 && newPassword.length > 0 && confirmPassword.length > 0;

  return (
    <>
      <NotificationContainer />
      <div className="change-password-modal-overlay" onClick={onClose}>
        <div className="change-password-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="change-password-modal-title">{t('changePassword.title')}</div>

          <div className="change-password-modal-field">
            <label>{t('changePassword.currentPassword')}</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="current-password"
            />
          </div>

          <div className="change-password-modal-field">
            <label>{t('changePassword.newPassword')}</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password"
            />
          </div>

          <div className="change-password-modal-field">
            <label>{t('changePassword.confirmPassword')}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="confirm-password"
            />
          </div>

          {validationError && (
            <div className="change-password-modal-error" data-testid="validation-error">
              {validationError}
            </div>
          )}

          <div className="change-password-modal-actions">
            <button onClick={onClose} className="btn-cancel">
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSubmit}
              className={`btn-primary ${isLoading ? 'btn-loading' : ''}`}
              disabled={!isFormValid || isLoading}
            >
              {isLoading ? (
                <><span className="btn-spinner"></span> {t('common.saving')}</>
              ) : (
                t('changePassword.changePassword')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChangePasswordModal;
